import React, { ReactNode, useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { observer } from "mobx-react-lite"
import customerStore from "features/organizations/customers/store/customer-store"
import { FormattedMessage, Button, useIntl, PageTitle, Table, ToastLevel, useToast, ContextMenu } from "@metsooutotec/modes-react-components"
import { ReactSVG } from "react-svg"

import { style } from "../view/asset-section-view-styles"
import { NavigationDeleteEntityOptions, NavigationModalOptions } from "components/utils/modal-helper"
import { GenerateBreadcrumbs } from "features/organizations/customers/utils/customers-bread-crumbs"
import { getAssetSectionPdf, getAssetSectionSVG } from "../../services/asset-section-services"
import { downloadFileFromBlobUrl } from "components/utils/files-utils"
import { NotFound } from "components/utils/not-found"
import {
    itemViewCellStyles,
    itemViewDetailsContainerStyles,
    itemViewDetailsTableStyles,
    itemViewListExtraTopPaddingStyles,
} from "components/common-style/common-style"
import LowSpinner from "components/common/low-spinner"
import EntityDeleteModal from "features/organizations/customers/components/entity-delete-modal"
import { ModalTypes } from "features/organizations/customers/utils/customer-modal-helper"

const AssetSectionView: React.FC = observer(() => {
    const { formatMessage } = useIntl()
    const navigate = useNavigate()
    const { setMessage } = useToast()
    const [assetSectionImage, setAssetSectionImage] = useState<string | null>(null)
    const [loadingAssetImage, setLoadingAssetImage] = useState<boolean>(false)
    const { customerId, siteId, assetId, assetSectionId } = useParams()
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false)
    const [downloadLoading, setDownloadLoading] = useState<boolean>(false)
    const location = useLocation()
    const params: NavigationModalOptions = location.state
    const { customerMap, siteMap, assetMap, assetSectionMap } = customerStore

    const customer = customerMap[customerId || ""]
    const site = siteMap[siteId || ""]
    const asset = assetMap[assetId || ""]
    const assetSection = assetSectionMap[assetSectionId || ""]
    const breadcrumbsPath = GenerateBreadcrumbs(customer, site, asset, assetSection)

    const fetchImage = async () => {
        setLoadingAssetImage(true)
        const imageUrl = await getAssetSectionSVG(assetSectionId as string)
        setAssetSectionImage(imageUrl)
        setLoadingAssetImage(false)
    }
    const showNotifier = (text: string) => {
        setMessage(text, ToastLevel.SUCCESS, 6000)
    }
    const memoizedFetchImage = useMemo(() => fetchImage, [assetSectionId])
    useEffect(() => {
        memoizedFetchImage()
    }, [memoizedFetchImage])

    useEffect(() => {
        if (params?.type === "created") {
            showNotifier("New Asset Section created successfully")
        }
        if (params?.type === "edited") {
            showNotifier("All changes made successfully")
        }
        window.history.replaceState({}, document.title)
    }, [params, assetSection])

    const deleteAssetSectionModal = {
        open: () => {
            setIsDeleteModalOpen(true)
        },
        close: (params: NavigationDeleteEntityOptions) => {
            setIsDeleteModalOpen(false)
            if (params.deleted) {
                navigate(`/customers/${customerId}/sites/${siteId}/assets/${assetId}`, { state: params })
            }
        },
    }
    const downloadFile = (
        setDownloadLoading: (loading: boolean) => void,
        fetchFunction: (assetId: string) => Promise<string>,
        fileNameEndWith: string,
        fileExtension: string,
    ) => {
        setDownloadLoading(true)
        fetchFunction(assetSectionId as string)
            .then(blobUrl => {
                if (blobUrl) {
                    downloadFileFromBlobUrl(blobUrl, `AssetSection_${assetSectionId}-${fileNameEndWith}.${fileExtension}`)
                } else {
                    setMessage(`The requested ${fileExtension.toUpperCase()} file could not be found`, ToastLevel.WARNING, 6000)
                }
            })
            .catch(err => {
                console.error("An error occurred:", err)
                setMessage(`An error occurred while downloading the ${fileExtension.toUpperCase()} file`, ToastLevel.ALERT, 6000)
            })
            .finally(() => {
                setDownloadLoading(false)
            })
    }
    enum downloadOptions {
        SVG = "svg",
        PDF = "pdf",
    }
    const downloadFiles = {
        svgFile: () => downloadFile(setDownloadLoading, getAssetSectionSVG, "2D", downloadOptions.SVG),
        pdfFile: () => downloadFile(setDownloadLoading, getAssetSectionPdf, "Pdf", downloadOptions.PDF),
    }

    const onContextMenuChange = (value: string | string[]) => {
        if (value === downloadOptions.PDF) downloadFiles.pdfFile()
        else if (value === downloadOptions.SVG) downloadFiles.svgFile()
    }
    const navigateToEditor = () => {
        navigate(`/customers/${customerId}/sites/${siteId}/assets/${assetId}/assetsections/${assetSectionId}/editor`)
    }
    const rightPageButtons: ReactNode = (
        <div className='asset-section-view-right-buttons'>
            {downloadLoading ? (
                <Button icon='icon-edit-16' labelPosition='right' disabled loading primary>
                    Downloading
                </Button>
            ) : (
                <ContextMenu
                    className='download-menu'
                    menuText='Downloads'
                    icon='icon-download-16'
                    onChange={onContextMenuChange}
                    options={[
                        { text: formatMessage("assetSection.downloadPdf"), value: downloadOptions.PDF },
                        { text: formatMessage("assetSection.downloadSvg"), value: downloadOptions.SVG },
                    ]}
                />
            )}
            <Button icon='icon-edit-16' labelPosition='right' onClick={navigateToEditor} primary>
                {formatMessage("assetSection.edit")}
            </Button>
            <Button icon='icon-trash-16' labelPosition='left' onClick={deleteAssetSectionModal.open} secondary>
                {formatMessage("assetSection.delete")}
            </Button>
        </div>
    )
    return !assetSection ? (
        <NotFound id={assetSectionId} />
    ) : (
        <>
            <style>{style}</style>
            <PageTitle breadcrumbsPath={breadcrumbsPath} onPathChange={navigate} title={assetSection.name} extraItems={rightPageButtons} />
            <div style={itemViewDetailsContainerStyles}>
                <Table style={itemViewDetailsTableStyles} tight>
                    <Table.Body>
                        <Table.Row>
                            <Table.HeaderCell style={{ ...itemViewCellStyles, width: "20%" }}>
                                <FormattedMessage id='assetSection.description' />
                            </Table.HeaderCell>
                            <Table.Cell style={itemViewCellStyles}>{assetSection.description ? assetSection.description : "Empty"}</Table.Cell>
                        </Table.Row>
                    </Table.Body>
                </Table>

                <div style={{ marginTop: "1em" }}>
                    <div style={itemViewListExtraTopPaddingStyles}>
                        {loadingAssetImage ? (
                            <LowSpinner id='assetSection.imageLoading' />
                        ) : (
                            <ReactSVG src={assetSectionImage as string} evalScripts='once' className='wrapper' />
                        )}
                    </div>
                </div>
            </div>
            <EntityDeleteModal
                isOpen={isDeleteModalOpen}
                onClose={deleteAssetSectionModal.close}
                entity={assetSection}
                modalType={ModalTypes.AssetSectionModal}
                customerId={customer?.id}
                siteId={siteId}
                assetId={assetId}
            />
        </>
    )
})

export default AssetSectionView
